import type { Reducer } from "react";
import type { WizardStepMeta } from "./WizardContext";

type WizardReducerState = {
  activeStep: string;
  history: string[];
  direction: "forward" | "backward";
  steps: WizardStepMeta[];
  initialStep: string;
};

type WizardReducerAction =
  | { type: "next" }
  | { type: "prev" }
  | { type: "back" }
  | { type: "goTo"; step: string; replace?: boolean }
  | { type: "register"; meta: WizardStepMeta }
  | { type: "unregister"; name: string }
  | { type: "sync"; step: string }
  | { type: "reset" };

const isDeclaredBefore = (
  step: string,
  activeStep: string,
  steps: WizardStepMeta[],
) => {
  const stepIndex = steps.findIndex((meta) => meta.name === step);
  const activeStepIndex = steps.findIndex((meta) => meta.name === activeStep);

  if (stepIndex === -1 || activeStepIndex === -1) {
    return false;
  }

  return stepIndex < activeStepIndex;
};

const getStepNeighbour = (
  step: string,
  steps: WizardStepMeta[],
  direction: "forward" | "backward",
) => {
  const stepIndex = steps.findIndex((meta) => meta.name === step);

  if (stepIndex === -1) {
    console.warn(`Step not found: ${step}`);
    return;
  }

  const offset = direction === "forward" ? 1 : -1;
  return steps[stepIndex + offset];
};

export const wizardReducer: Reducer<WizardReducerState, WizardReducerAction> = (
  state,
  action,
) => {
  switch (action.type) {
    case "next":
    case "prev": {
      const neighbour = getStepNeighbour(
        state.activeStep,
        state.steps,
        action.type === "next" ? "forward" : "backward",
      );

      if (!neighbour) return state;

      return wizardReducer(state, { type: "goTo", step: neighbour.name });
    }
    case "back": {
      const registeredSteps = new Set(state.steps.map((s) => s.name));
      // filter out steps that are not registered or have been unregistered due
      // to being unmounted at the component level
      const validSteps = state.history.filter((name) =>
        registeredSteps.has(name),
      );

      const previous =
        validSteps.at(-1) ??
        getStepNeighbour(state.activeStep, state.steps, "backward")?.name;

      if (previous === undefined) {
        return state;
      }

      return {
        ...state,
        activeStep: previous,
        history: validSteps.slice(0, -1),
        direction: "backward",
      };
    }
    case "goTo": {
      if (action.step === state.activeStep) return state;

      if (!state.steps.some((s) => s.name === action.step)) {
        console.warn(
          `[Wizard] goTo("${action.step}"): step is not registered (yet?).`,
        );
      }

      let history: string[];
      if (!action.replace) {
        history = [...state.history, state.activeStep];
      } else if (state.history.at(-1) === action.step) {
        // a replace back to the step we came from is a return trip: pop it,
        // otherwise history's top would equal the active step and the next
        // back() would navigate to itself
        history = state.history.slice(0, -1);
      } else {
        history = state.history;
      }

      return {
        ...state,
        activeStep: action.step,
        history,
        direction: isDeclaredBefore(action.step, state.activeStep, state.steps)
          ? "backward"
          : "forward",
      };
    }
    case "register": {
      const index = state.steps.findIndex((s) => s.name === action.meta.name);

      if (index === -1) {
        return { ...state, steps: [...state.steps, action.meta] };
      }

      const steps = [...state.steps];
      steps[index] = action.meta;

      return { ...state, steps };
    }
    case "unregister": {
      return {
        ...state,
        steps: state.steps.filter((m) => m.name !== action.name),
      };
    }
    case "sync": {
      if (action.step === state.activeStep) {
        return state;
      }

      if (state.history.at(-1) === action.step) {
        return {
          ...state,
          activeStep: action.step,
          history: state.history.slice(0, -1),
          direction: "backward",
        };
      }

      return {
        ...state,
        activeStep: action.step,
        history: [...state.history, state.activeStep],
        direction: "forward",
      };
    }
    case "reset": {
      if (
        state.activeStep === state.initialStep &&
        state.history.length === 0
      ) {
        return state;
      }

      return {
        ...state,
        activeStep: state.initialStep,
        history: [],
        direction: "backward",
      };
    }
  }
};
